import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { FeeLedgerTotals, FeeStatus } from '@/types/fees';
import { FEE_STATUS_LABEL, FEE_STATUS_TONE } from '../constants';

export interface FigureProps {
  label: string;
  value: string;
  /**
   * Tints the number. Only where the figure itself is the signal.
   *
   * The money words are the console's: `blue` is money in, `amber` money still
   * out, `indigo` a concession, and plain slate the totals that are neither.
   * They match the gauge in the banner deliberately — the same four figures
   * appear in both, and two colour schemes for one set of numbers is how a
   * reader stops trusting either. `ok`/`warn`/`crit` remain for the places a
   * figure really is a verdict.
   */
  tone?: 'default' | 'ok' | 'warn' | 'crit' | 'blue' | 'indigo' | 'amber' | 'emerald' | 'rose';
  hint?: string;
}

const HIGHLIGHT_STYLES = {
  default: {
    box: 'bg-slate-100/90 dark:bg-slate-900 border-slate-200/90 dark:border-slate-800',
    label: 'text-slate-600 dark:text-slate-400 font-extrabold',
    value: 'text-slate-700 dark:text-slate-100',
  },
  blue: {
    box: 'bg-blue-50/90 dark:bg-slate-900 border-blue-200/90 dark:border-slate-800 shadow-xs',
    label: 'text-blue-700 dark:text-blue-400 font-extrabold',
    value: 'text-blue-950 dark:text-white',
  },
  indigo: {
    box: 'bg-indigo-50/90 dark:bg-slate-900 border-indigo-200/90 dark:border-slate-800 shadow-xs',
    label: 'text-indigo-700 dark:text-indigo-400 font-extrabold',
    value: 'text-indigo-950 dark:text-white',
  },
  amber: {
    box: 'bg-amber-50/90 dark:bg-slate-900 border-amber-200/90 dark:border-slate-800 shadow-xs',
    label: 'text-amber-800 dark:text-amber-400 font-extrabold',
    value: 'text-amber-950 dark:text-white',
  },
  emerald: {
    box: 'bg-emerald-50/90 dark:bg-slate-900 border-emerald-200/90 dark:border-slate-800 shadow-xs',
    label: 'text-emerald-800 dark:text-emerald-400 font-extrabold',
    value: 'text-emerald-950 dark:text-white',
  },
  rose: {
    box: 'bg-rose-50/90 dark:bg-slate-900 border-rose-200/90 dark:border-slate-800 shadow-xs',
    label: 'text-rose-800 dark:text-rose-400 font-extrabold',
    value: 'text-rose-950 dark:text-white',
  },
  ok: {
    box: 'bg-emerald-50/90 dark:bg-slate-900 border-emerald-200/90 dark:border-slate-800 shadow-xs',
    label: 'text-emerald-800 dark:text-emerald-400 font-extrabold',
    value: 'text-emerald-950 dark:text-white',
  },
  warn: {
    box: 'bg-amber-50/90 dark:bg-slate-900 border-amber-200/90 dark:border-slate-800 shadow-xs',
    label: 'text-amber-800 dark:text-amber-400 font-extrabold',
    value: 'text-amber-950 dark:text-white',
  },
  crit: {
    box: 'bg-rose-50/90 dark:bg-slate-900 border-rose-200/90 dark:border-slate-800 shadow-xs',
    label: 'text-rose-800 dark:text-rose-400 font-extrabold',
    value: 'text-rose-950 dark:text-white',
  },
} as const;

export function Figure({ label, value, tone = 'default', hint }: FigureProps) {
  const style = HIGHLIGHT_STYLES[tone] ?? HIGHLIGHT_STYLES.default;

  return (
    <div className={cn('min-w-0 rounded-2xl border p-3.5 sm:p-4 transition-all', style.box)}>
      <dt className={cn('text-xs uppercase tracking-wider font-extrabold', style.label)}>
        {label}
      </dt>
      <dd className={cn('mt-1 text-2xl font-black tabular-nums tracking-tight', style.value)}>
        {value}
      </dd>
      {hint && <p className="mt-1 text-xs font-medium opacity-80">{hint}</p>}
    </div>
  );
}

export interface LedgerFiguresProps {
  ledger: FeeLedgerTotals;
  status?: FeeStatus;
  className?: string;
}

export function LedgerFigures({ ledger, status, className }: LedgerFiguresProps) {
  const balance = Number(ledger.balance);

  return (
    <div className={cn('rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5 shadow-xs', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">Account for the session</h3>
        {status && <Badge tone={FEE_STATUS_TONE[status]}>{FEE_STATUS_LABEL[status]}</Badge>}
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Figure label="Charged" value={formatCurrency(ledger.charged)} tone="default" />
        <Figure
          label="Concession"
          value={
            Number(ledger.concession) > 0 ? `− ${formatCurrency(ledger.concession)}` : '—'
          }
          tone={Number(ledger.concession) > 0 ? 'indigo' : 'default'}
        />
        <Figure label="Payable" value={formatCurrency(ledger.payable)} tone="default" />
        <Figure label="Collected" value={formatCurrency(ledger.collected)} tone="blue" />
        <Figure
          label="Balance"
          value={formatCurrency(ledger.balance)}
          tone={balance > 0 ? 'warn' : 'blue'}
        />
      </dl>
    </div>
  );
}
